import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';

import { AppMetaClient, AppMetaUserViewCommand, AppMetaUserViewItem, AppRouteDto, AppUserRouteDto, AppUserRouteViewDto, DocumentLibDto, RoleDto } from 'src/app/api';
import { CerFormUserViewType, CerMetaUserView } from '@form/cer-form-persistence.service';

import { CerNavigationNode, GeneratedNavigationSettings, GeneratedRouteSettings } from 'src/app/app.module-generated';
import { DocumentLibFinderFormRouteHelper } from '@core/document-lib/document-lib-finder.form';

import { CerAppNavigationNodesUserLoader } from 'src/cer-app/cer-app-route/cer-app-navigation-nodes-user-loader';
import { CerAppNavigationNodesLoader } from 'src/cer-app/cer-app-route/cer-app-navigation-nodes-loader';
import { AlertDialogArgs } from '@syncfusion/ej2-angular-popups';

export class CerUserRoute {
  public routeUrl: string;
  public defaultView: string;
  public views: CerMetaUserView[] = [];
};

@Injectable({
  providedIn: 'root'
})
export class CerSessionUserNavigationService implements OnDestroy {

  public navigation$: BehaviorSubject<CerNavigationNode[]> = new BehaviorSubject<CerNavigationNode[]>(null);
  public userRoutes$: BehaviorSubject<CerUserRoute[]> = new BehaviorSubject<CerUserRoute[]>([]);

  constructor(private appMetaClient: AppMetaClient) {
  }

  private subscriptionManager$: Subscription = new Subscription();

  ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  public navigationLoad(userRoles: RoleDto[], documentLibs: DocumentLibDto[]): void {
    const routeSettings = DocumentLibFinderFormRouteHelper.setupRoutes(GeneratedRouteSettings, documentLibs);
    const navigationAll = new CerAppNavigationNodesLoader(routeSettings, GeneratedNavigationSettings).load();
    const navigation = new CerAppNavigationNodesUserLoader(userRoles ?? []).load(navigationAll);
    this.navigation$.next(navigation);
  }

  public onUserRoutes(routesDto: AppRouteDto[], userRoutesDto: AppUserRouteDto[], userRouteViewsDto: AppUserRouteViewDto[]) {
    var userRoutes: CerUserRoute[] = [];
    userRoutesDto?.forEach(userRouteDto => {
      var routeDto = routesDto.find(r => r.id == userRouteDto.routeId);
      if (routeDto) {
        userRoutes.push(this.onUserRoute(routeDto, userRouteDto, userRouteViewsDto));
      }
    });
    this.userRoutes$.next(userRoutes);
  }

  private onUserRoute(routeDto: AppRouteDto, userRouteDto: AppUserRouteDto, userRouteViewsDto: AppUserRouteViewDto[]): CerUserRoute {
    var defaultRoute: CerUserRoute = this.userRouteDefaultGet(routeDto.url, userRouteDto.defaultView);
    defaultRoute.defaultView = userRouteDto.defaultView;

    userRouteViewsDto?.filter(urv => urv.userRouteId == userRouteDto.id).forEach(urv => {
      var view: CerMetaUserView = new CerMetaUserView();
      view.name = urv.name;
      view.isPrimary = urv.isPrimary;
      view.isDefault = urv.isDefault;
      view.formPersistenceData = urv.formPersistenceData;
      view.data = urv.params;
      view.type = urv.params != null ? 'design' : 'custom';
      defaultRoute.views.push(view);
    });

    return defaultRoute;
  }

  public userRouteGetOrDefault(routeUrl: string): CerUserRoute {
    return this.userRouteGet(routeUrl) ?? this.userRouteDefaultGet(routeUrl);
  }

  /*
  public setRoute(routeUrl: string, route: CerUserRoute) {
    
    var routers = this.routes$.getValue();
    if (routers) {
      var index = routers.findIndex(r => r.routeUrl == route.routeUrl);
      if (index >= 0) {
        routers[index] = route;
      }
      else {
        routers.push(route);
      }
    }
  }*/

  public hasRoute(routeUrl: string): boolean {
    return (this.userRouteGet(routeUrl) != null);
  }

  private userRouteGet(routeUrl: string): CerUserRoute {
    return this.userRoutes$.getValue().find(r => r.routeUrl == routeUrl);
  }

  public userRouteDefaultGet(routeUrl: string, defaultViewName = this.getViewNameStandard()): CerUserRoute {
    var route: CerUserRoute = { routeUrl: routeUrl, defaultView: defaultViewName ?? this.getViewNameStandard(), views: [] };
    route.views.push(<CerMetaUserView>{ name: this.getViewNameStandard(), type: 'standard', formPersistenceData: null });
    route.views.push(<CerMetaUserView>{ name: 'Alle felter', type: 'complete', formPersistenceData: null });
    return route;
  }

  public viewByNameGet(routeUrl: string, viewName: string = null): CerMetaUserView {
    var route = this.userRouteGetOrDefault(routeUrl);
    var view = this.viewGet(route, viewName);
    return view;
  }

  public viewByTypeGet(routeUrl: string, viewType: CerFormUserViewType): CerMetaUserView {
    var route = this.userRouteGetOrDefault(routeUrl);
    var view = route?.views?.find(v => v.type == viewType);
    return view;
  }

  public viewGet(route: CerUserRoute, viewName: string = null): CerMetaUserView {
    var view = route?.views?.find(v => v.name == (viewName ? viewName : route.defaultView));
    return view;
  }

  public viewCustomSave(url: string, viewName: string, isPrimary: boolean, isDefault: boolean, formPersistenceData: string, viewNameOrig: string = null) {

    if (viewName == this.getViewNameStandard() || viewName == null || viewName.length == 0 || formPersistenceData == null) {
      return;
    }
    const customView: CerMetaUserView = {
      name: viewName,
      type: 'custom',
      isPrimary: isPrimary,
      isDefault: isDefault,
      formPersistenceData: formPersistenceData
    };
    this.viewAsToUserRoute(url, customView, viewNameOrig);
  }

  public viewDesignedAdd(url: string, viewName: string, data: any) {
    const designedView: CerMetaUserView = {
      name: viewName,
      type: 'design',
      isPrimary: true,
      isDefault: false,
      formPersistenceData: null,
      data: data
    };
    this.viewAsToUserRoute(url, designedView);
  }


  /*
  public setRouterView(routeUrl: string, viewName: string = null, formPersistenceData: string): CerMetaUserView {
7    var route = this.getRouteOrDefault(routeUrl, true);
    return this.setView(route, viewName, formPersistenceData);
  }
  */

  public viewAsToUserRoute(url: string, view: CerMetaUserView, viewNameOrig: string = null) {
    const route = this.userRouteGetOrDefault(url);
    var index = route.views.findIndex(v => v.name == view.name);
    if (index < 0 && viewNameOrig?.length > 0) {
      index = route.views.findIndex(v => v.name == viewNameOrig);
    }
    if (index >= 0) {
      route.views[index] = view;
    }
    else {
      route.views.push(view);
    }
    if (view.type == 'custom') {
      this.apiUserRouteSave(route);
    }
  }

  private static viewNameStandard = 'Standard';
  public getViewNameStandard(): string {
    return CerSessionUserNavigationService.viewNameStandard;
  }


  //#region API/DTO
  public async apiUserRouteGet(url: string): Promise<CerUserRoute> {
    var vm = await firstValueFrom(this.appMetaClient.getUserViews(url));
    return this.apiUserRouteVmLoad(vm.route, vm.userRoute, vm.userRouteViews);
  }

  public apiUserRouteVmLoad(route: AppRouteDto, userRoute: AppUserRouteDto, userRouteViews: AppUserRouteViewDto[]): CerUserRoute {
    const userRoutesDto = this.userRoutes$.getValue();
    var routeIdx = userRoutesDto?.findIndex(r => r.routeUrl == route.url);
    if (routeIdx != -1) {
      userRoutesDto.splice(routeIdx, 1);
    }
    const newUserRoute = this.onUserRoute(route, userRoute, userRouteViews)
    userRoutesDto.push(newUserRoute);
    this.userRoutes$.next(userRoutesDto);
    return newUserRoute;
  }

  private apiUserRouteSave(userRoute: CerUserRoute) {
    const views: AppMetaUserViewItem[] = [];

    userRoute.views
      .filter(v => v.type == 'custom')
      .forEach(v => {
        views.push(new AppMetaUserViewItem({
          name: v.name,
          isPrimary: v.isPrimary,
          isDefault: v.isDefault,
          formPersistenceData: v.formPersistenceData
        }));
      });

    var cmd: AppMetaUserViewCommand = new AppMetaUserViewCommand({
      routeUrl: userRoute.routeUrl,
      defaultView: userRoute.defaultView,
      views: views
    });

    this.appMetaClient.postUserViews(cmd).subscribe({
      next: ((ok) => { }),
      error: ((error) => { console.log(error) })
    });
  }
  //#endregion API/DTO
}
